// Coleta os dados da carteirinha (instituição, curso, nível de ensino, foto)
// que o provisionamento exige (colunas NOT NULL de users_carteirinhas + foto em
// carteirinhas_imagem). Usado no checkout de renovação (/produtos). Só é
// renderizado quando há DIGITAL a criar — por isso o switch "Matricular na
// FUNEMG" (curso gratuito = direito da carteirinha digital) mora aqui: física
// upgrade não passa por este form, logo não oferece curso. `dados`/`setDados`
// são controlados pelo pai.
import { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { TextField, Field } from './Fields';
import FotoCropUpload from './FotoCropUpload';
import ComprovanteUpload from './ComprovanteUpload';
import { listarCursosFunemgApi } from '../api';

// Níveis de ensino do UEB (id numérico gravado em users_carteirinhas.escolaridade;
// o mesmo mapa do backend — utils/estudante.escolaridadeMap).
export const ESCOLARIDADES = [
    { id: 1, label: 'Ensino Fundamental' },
    { id: 3, label: 'Ensino Médio' },
    { id: 12, label: 'Técnico' },
    { id: 5, label: 'Ensino Superior' },
    { id: 7, label: 'Pós-graduação' },
    { id: 9, label: 'Mestrado' },
    { id: 11, label: 'Doutorado' },
];

const FUNEMG_INSTITUICAO = 'FUNEMG';
const TECNICO_ID = 12; // "Técnico"
const nomeCursoEad = (c) => ((c?.nome_carteirinha || '').trim() || (c?.titulo || '').trim());

export const DADOS_CARTEIRINHA_VAZIO = {
    instituicao: '', curso: '', escolaridade: '', foto: '', comprovante: '',
    matricularFunemg: false, cursoEadId: '',
};

// Verdadeiro quando todos os campos obrigatórios estão preenchidos.
export function dadosCarteirinhaCompletos(d) {
    return !!(d && d.instituicao?.trim() && d.curso?.trim() && d.escolaridade && d.foto);
}

// Monta o objeto dadosCadastro que o backend espera (escolaridade numérica).
// `comprovante` é OPCIONAL. `matricular_funemg`/`curso_ead_id` só vão quando o
// aluno ligou o switch (aí o backend matricula no EAD após o pagamento digital).
export function montarDadosCadastro(d) {
    return {
        instituicao: d.instituicao.trim(),
        curso: d.curso.trim(),
        escolaridade: Number(d.escolaridade),
        foto: d.foto,
        ...(d.matricularFunemg ? { matricular_funemg: true, curso_ead_id: d.cursoEadId } : {}),
        ...(d.comprovante ? { comprovante: d.comprovante } : {}),
    };
}

export default function DadosCarteirinhaForm({ dados, setDados, onErro }) {
    const setD = (campo) => (e) => setDados((p) => ({ ...p, [campo]: e.target.value }));
    const aviso = onErro || (() => {});
    const [cursosFunemg, setCursosFunemg] = useState([]);
    const matricularFunemg = !!dados.matricularFunemg;

    useEffect(() => {
        let vivo = true;
        listarCursosFunemgApi()
            .then((res) => { if (vivo) setCursosFunemg(Array.isArray(res?.data) ? res.data : []); })
            .catch(() => { if (vivo) setCursosFunemg([]); });
        return () => { vivo = false; };
    }, []);

    // Liga/desliga o atalho FUNEMG. Liga → instituição fixa, curso da lista do EAD
    // (Administração por padrão), nível Técnico e sem comprovante.
    const toggleFunemg = (on) => {
        if (on) {
            const c0 = cursosFunemg[0];
            setDados((p) => ({
                ...p, matricularFunemg: true, cursoEadId: c0 ? String(c0.id) : '',
                instituicao: FUNEMG_INSTITUICAO, curso: nomeCursoEad(c0), escolaridade: TECNICO_ID, comprovante: '',
            }));
        } else {
            setDados((p) => ({ ...p, matricularFunemg: false, cursoEadId: '', instituicao: '', curso: '', escolaridade: '' }));
        }
    };

    const selecionarCurso = (id) => {
        const c = cursosFunemg.find((x) => String(x.id) === String(id));
        setDados((p) => ({ ...p, cursoEadId: id, curso: nomeCursoEad(c) }));
    };

    // Se ligou antes dos cursos chegarem, seleciona o 1º assim que carregarem.
    useEffect(() => {
        if (!matricularFunemg || !cursosFunemg.length || dados.cursoEadId) return;
        const c0 = cursosFunemg[0];
        setDados((p) => ({ ...p, cursoEadId: String(c0.id), curso: nomeCursoEad(c0) }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matricularFunemg, cursosFunemg, dados.cursoEadId]);

    const cursoSel = cursosFunemg.find((x) => String(x.id) === String(dados.cursoEadId)) || null;

    return (
        <div className="ueb-dc">
            <label className="ueb-dc-switch">
                <input type="checkbox" checked={matricularFunemg} onChange={(e) => toggleFunemg(e.target.checked)} />
                <span className="ueb-dc-switch-track"><span className="ueb-dc-switch-thumb" /></span>
                <span className="ueb-dc-switch-label">Matricular na FUNEMG</span>
            </label>

            {matricularFunemg ? (
                <>
                    <div className="ueb-dc-funemg-aviso">
                        <CheckCircle2 size={18} />
                        <span>Sua carteira UEB dá direito a um <strong>cadastro gratuito</strong> em um curso da FUNEMG. A matrícula é liberada <strong>após o pagamento</strong>.</span>
                    </div>
                    <TextField id="dc-inst" label="Instituição de ensino" value={FUNEMG_INSTITUICAO} readOnly disabled />
                    <Field label="Curso" htmlFor="dc-curso">
                        <select id="dc-curso" className="pselect" value={dados.cursoEadId}
                            onChange={(e) => selecionarCurso(e.target.value)} disabled={!cursosFunemg.length}>
                            {!cursosFunemg.length && <option value="">Carregando cursos…</option>}
                            {cursosFunemg.map((c) => <option key={c.id} value={c.id}>{nomeCursoEad(c)}</option>)}
                        </select>
                    </Field>
                    <Field label="Nível de ensino" htmlFor="dc-escol">
                        <select id="dc-escol" className="pselect" value={TECNICO_ID} disabled>
                            <option value={TECNICO_ID}>Técnico</option>
                        </select>
                    </Field>
                    {cursoSel && (
                        <div className="ueb-dc-curso-card">
                            {cursoSel.capa_url && (
                                <img className="ueb-dc-curso-capa" src={cursoSel.capa_url} alt={nomeCursoEad(cursoSel)} loading="lazy" />
                            )}
                            {cursoSel.descricao && <p className="ueb-dc-curso-desc">{cursoSel.descricao}</p>}
                        </div>
                    )}
                </>
            ) : (
                <>
                    <TextField id="dc-inst" label="Instituição de ensino" placeholder="Ex.: UEB"
                        value={dados.instituicao} onChange={setD('instituicao')} />
                    <TextField id="dc-curso" label="Curso" placeholder="Ex.: Administração"
                        value={dados.curso} onChange={setD('curso')} />
                    <Field label="Nível de ensino" htmlFor="dc-escol">
                        <select id="dc-escol" className="pselect" value={dados.escolaridade} onChange={setD('escolaridade')}>
                            <option value="">Selecione…</option>
                            {ESCOLARIDADES.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
                        </select>
                    </Field>
                </>
            )}

            <FotoCropUpload
                value={dados.foto}
                onChange={(v) => setDados((p) => ({ ...p, foto: v }))}
                onErro={aviso}
            />
            {/* Matrícula na FUNEMG não pede comprovante (a matrícula é a prova). */}
            {!matricularFunemg && (
                <ComprovanteUpload
                    valor={dados.comprovante}
                    onChange={(v) => setDados((p) => ({ ...p, comprovante: v }))}
                    onErro={aviso}
                />
            )}
        </div>
    );
}
